import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { adminApi, cmsApi } from '../lib/api';
import { 
  LayoutDashboard, 
  Users, 
  BookOpen, 
  Trophy,
  TrendingUp,
  Plus,
  Search,
  Filter,
  MoreVertical,
  ArrowUpRight,
  ArrowDownRight,
  FileText,
  Settings,
  Mail,
  Video,
  Loader2,
  AlertCircle,
  Edit,
  Trash2,
  Eye
} from 'lucide-react';
import { 
  FadeIn, 
  AnimatedCard,
  StaggerContainer,
  StaggerItem,
  ProgressBar 
} from '../components/ui/AnimatedCard';

export function AdminDashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch real dashboard data
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: async () => {
      const res = await adminApi.getAnalytics();
      return res.data;
    },
  });

  // Fetch users
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const res = await adminApi.getUsers();
      return res.data;
    },
  });

  // Fetch CMS pages
  const { data: pagesData } = useQuery({
    queryKey: ['admin-pages'],
    queryFn: async () => {
      const res = await cmsApi.getPages();
      return res.data;
    },
  });

  // Fetch contacts
  const { data: contactsData } = useQuery({
    queryKey: ['admin-contacts'],
    queryFn: async () => {
      const res = await adminApi.getContacts();
      return res.data;
    },
  });

  // Fetch exams
  const { data: examsData, isLoading: examsLoading } = useQuery({
    queryKey: ['admin-exams'],
    queryFn: async () => {
      const res = await adminApi.getExams();
      return res.data;
    },
  });

  // Fetch CMS stats
  const { data: statsData } = useQuery({
    queryKey: ['cms-stats'],
    queryFn: async () => {
      const res = await cmsApi.getStats();
      return res.data;
    },
  });

  const stats = dashboardData || {
    totalUsers: 0,
    totalExamAttempts: 0,
    totalCompletedExams: 0,
  };

  const cmsStats = statsData || {
    totalUsers: 0,
    totalExams: 0,
    totalQuestions: 0,
    totalAttempts: 0,
  };

  const allUsers = usersData || [];
  const allPages = pagesData || [];
  const allContacts = contactsData || [];
  const allExams = examsData || [];

  const tabs = [
    { key: 'overview', label: 'Overview', icon: LayoutDashboard },
    { key: 'users', label: 'Users', icon: Users },
    { key: 'exams', label: 'Exams', icon: BookOpen },
    { key: 'cms', label: 'CMS', icon: FileText },
    { key: 'contacts', label: 'Contacts', icon: Mail },
  ];

  if (dashboardLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <FadeIn>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-500 mt-1">Welcome back, {user?.name}</p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/admin/cms"
              className="flex items-center gap-2 px-4 py-3 bg-white border border-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-colors"
            >
              <FileText className="w-4 h-4" />
              CMS
            </Link>
            <Link
              to="/admin/exams/create"
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Create Exam
            </Link>
          </div>
        </div>
      </FadeIn>

      {/* Stats Grid - Real Data */}
      <StaggerContainer className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { 
            label: 'Total Users', 
            value: cmsStats.totalUsers, 
            growth: 12.5,
            icon: Users,
            color: 'bg-blue-100 text-blue-600'
          },
          { 
            label: 'Total Exams', 
            value: cmsStats.totalExams, 
            growth: 8.2,
            icon: BookOpen,
            color: 'bg-purple-100 text-purple-600'
          },
          { 
            label: 'Total Attempts', 
            value: cmsStats.totalAttempts, 
            growth: 25.1,
            icon: Trophy,
            color: 'bg-amber-100 text-amber-600'
          },
          { 
            label: 'Questions', 
            value: cmsStats.totalQuestions, 
            growth: 5.3,
            icon: FileText,
            color: 'bg-green-100 text-green-600'
          },
        ].map((stat) => (
          <StaggerItem key={stat.label}>
            <AnimatedCard className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-10 h-10 ${stat.color} rounded-xl flex items-center justify-center`}>
                  <stat.icon className="w-5 h-5" />
                </div>
                <span className={`flex items-center gap-1 text-xs font-medium ${
                  stat.growth >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stat.growth >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {Math.abs(stat.growth)}%
                </span>
              </div>
              <div className="text-2xl font-bold text-slate-900">{stat.value.toLocaleString()}</div>
              <div className="text-sm text-slate-500">{stat.label}</div>
            </AnimatedCard>
          </StaggerItem>
        ))}
      </StaggerContainer>

      {/* Quick Actions */}
      <FadeIn>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Manage Pages', icon: FileText, href: '/admin/cms', color: 'bg-blue-50 text-blue-600 hover:bg-blue-100' },
            { label: 'Site Settings', icon: Settings, href: '/admin/settings', color: 'bg-purple-50 text-purple-600 hover:bg-purple-100' },
            { label: 'Contact Forms', icon: Mail, href: '/admin/contacts', color: 'bg-amber-50 text-amber-600 hover:bg-amber-100' },
            { label: 'Videos', icon: Video, href: '/admin/videos', color: 'bg-green-50 text-green-600 hover:bg-green-100' },
          ].map((action) => (
            <Link
              key={action.label}
              to={action.href}
              className={`flex items-center gap-3 p-4 rounded-xl transition-colors ${action.color}`}
            >
              <action.icon className="w-5 h-5" />
              <span className="font-medium text-sm">{action.label}</span>
            </Link>
          ))}
        </div>
      </FadeIn>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {/* OVERVIEW */}
        {activeTab === 'overview' && (
          <FadeIn>
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Recent Users */}
              <AnimatedCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-900">Recent Users ({allUsers.length})</h3>
                  <button onClick={() => setActiveTab('users')} className="text-sm text-primary-600 hover:text-primary-700">
                    View All
                  </button>
                </div>
                <div className="space-y-3">
                  {allUsers.slice(0, 5).map((u: any) => (
                    <div key={u.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {u.name?.charAt(0) || '?'}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-slate-900">{u.name}</div>
                        <div className="text-sm text-slate-500">{u.phone || u.email}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-slate-600">{u.points || 0} pts</div>
                        <div className="text-xs text-slate-400">{u.isAdmin ? 'Admin' : 'Student'}</div>
                      </div>
                    </div>
                  ))}
                  {allUsers.length === 0 && (
                    <div className="text-center py-8 text-slate-400">No users found</div>
                  )}
                </div>
              </AnimatedCard>

              {/* CMS Pages */}
              <AnimatedCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-900">CMS Pages ({allPages.length})</h3>
                  <button onClick={() => setActiveTab('cms')} className="text-sm text-primary-600 hover:text-primary-700">
                    Manage
                  </button>
                </div>
                <div className="space-y-3">
                  {allPages.slice(0, 5).map((page: any) => (
                    <div key={page.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <div>
                        <div className="font-medium text-slate-900">{page.title}</div>
                        <div className="text-sm text-slate-500">/{page.slug}</div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        page.isPublished ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {page.isPublished ? 'Published' : 'Draft'}
                      </span>
                    </div>
                  ))}
                  {allPages.length === 0 && (
                    <div className="text-center py-8 text-slate-400">No pages found</div>
                  )}
                </div>
              </AnimatedCard>

              {/* Contact Submissions */}
              <AnimatedCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-900">Contact Submissions ({allContacts.length})</h3>
                  <button onClick={() => setActiveTab('contacts')} className="text-sm text-primary-600 hover:text-primary-700">
                    View All
                  </button>
                </div>
                <div className="space-y-3">
                  {allContacts.slice(0, 3).map((contact: any) => (
                    <div key={contact.id} className="p-3 bg-slate-50 rounded-xl">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-medium text-slate-900">{contact.name}</div>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          contact.status === 'new' ? 'bg-red-100 text-red-700' :
                          contact.status === 'read' ? 'bg-blue-100 text-blue-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {contact.status}
                        </span>
                      </div>
                      <div className="text-sm text-slate-500">{contact.subject}</div>
                      <div className="text-xs text-slate-400 mt-1">{contact.email}</div>
                    </div>
                  ))}
                  {allContacts.length === 0 && (
                    <div className="text-center py-8 text-slate-400">No contact submissions</div>
                  )}
                </div>
              </AnimatedCard>

              {/* Analytics Summary */}
              <AnimatedCard className="p-6">
                <h3 className="font-bold text-slate-900 mb-4">Platform Analytics</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Exam Completion Rate</span>
                      <span className="font-semibold text-slate-900">
                        {stats.totalExamAttempts > 0 
                          ? Math.round((stats.totalCompletedExams / stats.totalExamAttempts) * 100) 
                          : 0}%
                      </span>
                    </div>
                    <ProgressBar progress={stats.totalExamAttempts > 0 ? (stats.totalCompletedExams / stats.totalExamAttempts) * 100 : 0} />
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="p-3 bg-slate-50 rounded-xl text-center">
                      <div className="text-2xl font-bold text-slate-900">{stats.totalExamAttempts}</div>
                      <div className="text-xs text-slate-500">Total Attempts</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl text-center">
                      <div className="text-2xl font-bold text-slate-900">{stats.totalCompletedExams}</div>
                      <div className="text-xs text-slate-500">Completed</div>
                    </div>
                  </div>
                </div>
              </AnimatedCard>
            </div>
          </FadeIn>
        )}

        {/* USERS */}
        {activeTab === 'users' && (
          <FadeIn>
            <AnimatedCard className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                  />
                </div>
                <button className="flex items-center gap-2 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors">
                  <Filter className="w-4 h-4" />
                  Filter
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">User</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Phone</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Points</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Streak</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Role</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.map((u: any) => (
                      <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold text-xs">
                              {u.name?.charAt(0) || '?'}
                            </div>
                            <div>
                              <div className="font-medium text-slate-900">{u.name}</div>
                              <div className="text-sm text-slate-500">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">{u.phone}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{u.points || 0}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{u.streakCount || 0}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            u.isAdmin ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {u.isAdmin ? 'Admin' : 'Student'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-500">
                          {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {allUsers.length === 0 && (
                  <div className="text-center py-8 text-slate-400">No users found</div>
                )}
              </div>
            </AnimatedCard>
          </FadeIn>
        )}

        {/* EXAMS */}
        {activeTab === 'exams' && (
          <FadeIn>
            <div className="space-y-4">
              <AnimatedCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-900">Exam Management ({allExams.length})</h3>
                  <Link
                    to="/admin/exams/create"
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Create Exam
                  </Link>
                </div>
                
                {examsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Exam</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Type</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Questions</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Duration</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allExams.map((exam: any) => (
                          <tr key={exam.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <div>
                                <div className="font-medium text-slate-900">{exam.title}</div>
                                <div className="text-sm text-slate-500">{exam.titleBn}</div>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                exam.type === 'live' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {exam.type}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                exam.status === 'published' ? 'bg-green-100 text-green-700' :
                                exam.status === 'running' ? 'bg-amber-100 text-amber-700' :
                                exam.status === 'completed' ? 'bg-slate-100 text-slate-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {exam.status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-600">{exam.questions?.length || 0}</td>
                            <td className="py-3 px-4 text-sm text-slate-600">{exam.durationMinutes} min</td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-1">
                                <Link
                                  to={`/exams/${exam.id}`}
                                  target="_blank"
                                  className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="View"
                                >
                                  <Eye className="w-4 h-4" />
                                </Link>
                                <button
                                  className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {allExams.length === 0 && (
                      <div className="text-center py-8 text-slate-400">No exams found</div>
                    )}
                  </div>
                )}
              </AnimatedCard>
            </div>
          </FadeIn>
        )}

        {/* CMS */}
        {activeTab === 'cms' && (
          <FadeIn>
            <AnimatedCard className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-slate-900">CMS Pages ({allPages.length})</h3>
                <Link
                  to="/admin/cms/create"
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Page
                </Link>
              </div>
              <div className="space-y-3">
                {allPages.map((page: any) => (
                  <div key={page.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div>
                      <div className="font-medium text-slate-900">{page.title}</div>
                      <div className="text-sm text-slate-500">/{page.slug}</div>
                      {page.metaDescription && (
                        <div className="text-xs text-slate-400 mt-1 line-clamp-1">{page.metaDescription}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        page.isPublished ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {page.isPublished ? 'Published' : 'Draft'}
                      </span>
                      <Link
                        to={`/page/${page.slug}`}
                        target="_blank"
                        className="p-2 hover:bg-white rounded-lg transition-colors"
                        title="View page"
                      >
                        <ArrowUpRight className="w-4 h-4 text-slate-400" />
                      </Link>
                    </div>
                  </div>
                ))}
                {allPages.length === 0 && (
                  <div className="text-center py-8 text-slate-400">No CMS pages found</div>
                )}
              </div>
            </AnimatedCard>
          </FadeIn>
        )}

        {/* CONTACTS */}
        {activeTab === 'contacts' && (
          <FadeIn>
            <AnimatedCard className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-slate-900">Contact Submissions ({allContacts.length})</h3>
              </div>
              <div className="space-y-3">
                {allContacts.map((contact: any) => (
                  <div key={contact.id} className="p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-slate-900">{contact.name}</div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        contact.status === 'new' ? 'bg-red-100 text-red-700' :
                        contact.status === 'read' ? 'bg-blue-100 text-blue-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {contact.status}
                      </span>
                    </div>
                    <div className="text-sm text-slate-600 mb-1">
                      <span className="font-medium">Subject:</span> {contact.subject}
                    </div>
                    <div className="text-sm text-slate-500 mb-2">{contact.message}</div>
                    <div className="flex items-center gap-4 text-xs text-slate-400">
                      <span>{contact.email}</span>
                      {contact.phone && <span>{contact.phone}</span>}
                      <span>{contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : '-'}</span>
                    </div>
                  </div>
                ))}
                {allContacts.length === 0 && (
                  <div className="text-center py-8 text-slate-400">No contact submissions yet</div>
                )}
              </div>
            </AnimatedCard>
          </FadeIn>
        )}
      </div>
    </div>
  );
}
